import React, { useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import './ImprimirTicket.css';
import logo from './../Logos/Priballl.png';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const ImprimirTicket = ({ saleInfo, onPrintClick }) => {
  const printRef = useRef();
  const wrapperRef = useRef();
  const [pdfLoading, setPdfLoading] = useState(false);

  const calculateTotalWithDiscount = () => {
    if (!saleInfo?.products?.length) return 0;
    
    const total = saleInfo.products.reduce((total, product) => {
      return total + (product.price * product.quantity);
    }, 0);
    
    // Usar la nueva estructura de descuento
    if (saleInfo.discountAmount) {
      return total - saleInfo.discountAmount;
    }
    
    // Fallback para el formato anterior
    const discountAmount = (total * (saleInfo.discount || 0)) / 100;
    return total - discountAmount;
  };

  const handlePrint = () => {
    if (onPrintClick && typeof onPrintClick === 'function') {
      onPrintClick(wrapperRef.current);
      return;
    }
    window.print();
  };

  // PDF idéntico al modal (incluye logo): captura el ticket con html2canvas y genera PDF
  const handleDownloadPDF = async () => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    setPdfLoading(true);
    let logoImg = wrapper.querySelector('.ticket-logo-small');
    const logoSrc = logoImg?.getAttribute('src');
    let originalLogoSrc = null;
    try {
      // Incluir logo como data URL para que aparezca en el PDF (evita CORS)
      if (logoImg && logoSrc && !logoSrc.startsWith('data:')) {
        originalLogoSrc = logoSrc;
        const dataUrl = await new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const c = document.createElement('canvas');
              c.width = img.naturalWidth;
              c.height = img.naturalHeight;
              c.getContext('2d').drawImage(img, 0, 0);
              resolve(c.toDataURL('image/png'));
            } catch (e) { reject(e); }
          };
          img.onerror = () => resolve(null);
          img.src = logoSrc;
        });
        if (dataUrl) logoImg.setAttribute('src', dataUrl);
      }
      // Forzar ancho A4 (794px ≈ 210mm) antes de capturar para que el PDF use todo el ancho de la hoja
      const targetEl = printRef.current || wrapper;
      const a4WidthPx = 794;
      const origWrapperWidth = wrapper.style.width;
      const origWrapperMaxWidth = wrapper.style.maxWidth;
      const origTargetWidth = targetEl.style.width;
      const origTargetMaxWidth = targetEl.style.maxWidth;
      wrapper.style.width = `${a4WidthPx}px`;
      wrapper.style.maxWidth = 'none';
      targetEl.style.width = `${a4WidthPx}px`;
      targetEl.style.maxWidth = 'none';
      const modalContent = wrapper.closest('.ticket-modal-content');
      const origModalMaxWidth = modalContent ? modalContent.style.maxWidth : '';
      if (modalContent) modalContent.style.maxWidth = 'none';
      await new Promise((r) => setTimeout(r, 50));
      const canvas = await html2canvas(targetEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      wrapper.style.width = origWrapperWidth;
      wrapper.style.maxWidth = origWrapperMaxWidth;
      targetEl.style.width = origTargetWidth;
      targetEl.style.maxWidth = origTargetMaxWidth;
      if (modalContent) modalContent.style.maxWidth = origModalMaxWidth;

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const w = pdfW;
      const h = pdfW * (canvas.height / canvas.width);
      pdf.addImage(imgData, 'PNG', 0, 0, w, h);
      let offset = pdfH;
      while (offset < h) {
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -offset, w, h);
        offset += pdfH;
      }
      const fileName = `comprobante-${saleInfo.orderNumber || 'ticket'}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('Error al generar PDF:', err);
    } finally {
      if (originalLogoSrc && logoImg) logoImg.setAttribute('src', originalLogoSrc);
      setPdfLoading(false);
    }
  };

  // Función para obtener medios de pago seleccionados
  const getSelectedPaymentMethods = () => {
    if (!saleInfo?.paymentMethods) return 'No especificado';
    
    return Object.entries(saleInfo.paymentMethods)
      .filter(([, value]) => value)
      .map(([key]) => (key === 'consignacion' ? 'Consignación (no cobrada)' : key.charAt(0).toUpperCase() + key.slice(1)))
      .join(', ') || 'No especificado';
  };

  if (!saleInfo) {
    return <div className="ticket-error">No hay información disponible</div>;
  }

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-AR');
  };

  const formatVencimientoCAE = (vencimiento_cae) => {
    if (!vencimiento_cae) return 'N/A';
    const s = String(vencimiento_cae).replace(/\D/g, '');
    if (s.length !== 8) return vencimiento_cae;
    return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
  };

  const tipoComprobante = saleInfo.invoiceOptions?.generateInvoice
    ? (saleInfo.invoiceOptions.invoiceType === 'remito' ? 'Remito' : 'Factura')
    : null;

  return (
    <div ref={wrapperRef} className="ticket-wrapper">
      <div ref={printRef} className="ticket-content">
        {/* Banner a todo el ancho (fuera del área con padding) */}
        {tipoComprobante && (
          <div className="ticket-type-banner">
            {tipoComprobante}
          </div>
        )}
        <div className="ticket-content-inner">
        {/* Header 3 columnas: Emisor | C en el centro | Datos (igual que factura) */}
        <div className="ticket-header-three-cols">
          <div className="ticket-col-emisor">
            <img src={logo} alt="Priball" className="ticket-logo-small" />
            <p><strong>Razón Social:</strong> Priball - Balones Deportivos</p>
            <p><strong>Domicilio:</strong> Ruta 9 y Junín, Bell Ville, Cba.</p>
            <p>Tel: (3537) 302888 · www.priball.com</p>
            <p>Monotributista · CUIT: 20-33602790-0</p>
            <p><strong>Ingresos Brutos:</strong> 280-404387</p>
            <p><strong>Fecha de inicio de Actividades:</strong> 01/11/2012</p>
            <p><strong>Punto de venta:</strong> {String(saleInfo.invoiceData?.punto_venta ?? saleInfo.invoiceOptions?.puntoVenta ?? 8).padStart(4, '0')}</p>
          </div>
          <div className="ticket-col-tipo">
            <div className="ticket-tipo-box">
              <span className="ticket-tipo-letra">{tipoComprobante === 'Remito' ? 'R' : 'C'}</span>
              <span className="ticket-tipo-codigo">{tipoComprobante === 'Remito' ? '—' : '011'}</span>
              <span className="ticket-tipo-monotributista">Monotributista</span>
            </div>
          </div>
          <div className="ticket-col-datos">
            <p className="ticket-factura-title">{tipoComprobante || 'COMPROBANTE'}</p>
            <p><span>Nº Orden:</span> <strong>{saleInfo.orderNumber || 'N/A'}</strong></p>
            <p><span>Fecha:</span> <strong>{formatDate(saleInfo.date)}</strong></p>
            <p><span>CUIT:</span> <strong>20-33602790-0</strong></p>
            <p><span>Ing. Brutos:</span> <strong>280-404387</strong></p>
            <p><span>Inicio Act.:</span> <strong>01/11/2012</strong></p>
          </div>
        </div>
        <hr className="ticket-sep" />

        {/* Comprobante / Orden — etiqueta: valor, compacto */}
        <div className="ticket-order">
          <div className="order-row"><strong>Nº Orden:</strong><span>{saleInfo.orderNumber || 'N/A'}</span></div>
          <div className="order-row"><strong>Fecha:</strong><span>{formatDate(saleInfo.date)}</span></div>
          {saleInfo.invoiceData && (
            <div className="order-row"><strong>Comprobante:</strong><span>{saleInfo.invoiceData.numero || saleInfo.invoiceData.number}</span></div>
          )}
        </div>
        <hr className="ticket-sep" />

        {/* Cliente — mismo criterio que factura */}
        {(saleInfo.customerData?.name || saleInfo.invoiceData) && (
          <>
            <div className="ticket-customer">
              <div className="customer-row"><strong>Sr.(es):</strong><span>{(saleInfo.customerData?.name || 'Consumidor Final').toUpperCase()}</span></div>
              {saleInfo.customerData?.cuit && <div className="customer-row"><strong>CUIT:</strong><span>{saleInfo.customerData.cuit}</span></div>}
            </div>
            <hr className="ticket-sep" />
          </>
        )}

        {/* Productos — tabla compacta, encabezado celeste como factura */}
        <div className="ticket-products">
          <div className="products-header2">
            <span>Cant.</span><span>Producto</span><span>Precio</span><span>Total</span>
          </div>
          {saleInfo.products?.map((product, index) => (
            <div key={index} className="product-row">
              <span>{product.quantity || 0}</span>
              <span>{product.model || 'Producto'}</span>
              <span>${formatNumber(product.price || 0)}</span>
              <span>${formatNumber((product.price || 0) * (product.quantity || 0))}</span>
            </div>
          ))}
        </div>
        <hr className="ticket-sep" />

        {/* Medios de pago */}
        <div className="ticket-payment">
          <div className="payment-row"><strong>Forma de pago:</strong><span>{getSelectedPaymentMethods()}</span></div>
        </div>
        <hr className="ticket-sep" />

        {/* Total — Subtotal, Descuento (tipo y valor), Total */}
        <div className="ticket-summary">
          {(saleInfo.discountAmount > 0 || saleInfo.discount > 0) && (
            <>
              <div className="summary-row"><span>Subtotal:</span><span>${formatNumber(saleInfo.subtotal || saleInfo.products?.reduce((t, p) => t + p.price * p.quantity, 0) || 0)}</span></div>
              {saleInfo.discountAmount > 0 && (
                <div className="summary-row">
                  <span>
                    {saleInfo.discountType === 'percentage' && saleInfo.discount != null
                      ? `Descuento (${formatNumber(saleInfo.discount)}%):`
                      : saleInfo.discountType === 'fixed'
                        ? 'Descuento (monto fijo):'
                        : 'Descuento:'}
                  </span>
                  <span>-${formatNumber(saleInfo.discountAmount)}</span>
                </div>
              )}
            </>
          )}
          <div className="summary-row total-row"><strong>Total: $ {formatNumber(saleInfo.totalPrice || calculateTotalWithDiscount())}</strong></div>
        </div>
        <hr className="ticket-sep" />

        {/* Email (tipo ya va en el encabezado) */}
        {saleInfo.invoiceOptions?.generateInvoice && saleInfo.invoiceOptions?.sendByEmail && saleInfo.invoiceOptions?.emailAddress && (
          <>
            <div className="ticket-invoice">
              <div className="invoice-row"><strong>Email:</strong><span>{saleInfo.invoiceOptions.emailAddress}</span></div>
            </div>
            <hr className="ticket-sep" />
          </>
        )}

        {/* Pie — siempre al final de la hoja: QR + Comprobante Autorizado | CAE, Vto, Gracias */}
        <div className="ticket-footer ticket-footer-afip">
          <div className="ticket-footer-left">
            {(saleInfo.invoiceData?.qr_data || saleInfo.invoiceData?.qrData) ? (
              <div className="ticket-qr">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(saleInfo.invoiceData.qr_data || saleInfo.invoiceData.qrData || '')}`} alt="QR AFIP" className="ticket-qr-img" />
                <p className="ticket-qr-label">Comprobante Autorizado</p>
              </div>
            ) : (
              <p className="ticket-qr-label">Comprobante Autorizado</p>
            )}
          </div>
          <div className="ticket-footer-right">
            {saleInfo.invoiceData?.cae && (
              <>
                <p><strong>CAE:</strong> {saleInfo.invoiceData.cae}</p>
                <p><strong>Vto. CAE:</strong> {formatVencimientoCAE(saleInfo.invoiceData.vencimiento_cae)}</p>
              </>
            )}
            <p className="ticket-thanks">¡Gracias por su compra!</p>
          </div>
        </div>

        {saleInfo.creditNoteData?.cae && (
          <>
            <hr className="ticket-sep" />
            <div className="ticket-type-banner" style={{ background: '#e8d4b8' }}>
              Nota de crédito C
            </div>
            <div className="ticket-footer ticket-footer-afip" style={{ marginTop: 8 }}>
              <div className="ticket-footer-left">
                {(saleInfo.creditNoteData?.qr_data || saleInfo.creditNoteData?.qrData) ? (
                  <div className="ticket-qr">
                    <img src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(saleInfo.creditNoteData.qr_data || saleInfo.creditNoteData.qrData || '')}`} alt="QR NC AFIP" className="ticket-qr-img" />
                    <p className="ticket-qr-label">NC — Comprobante Autorizado</p>
                  </div>
                ) : (
                  <p className="ticket-qr-label">NC — Comprobante Autorizado</p>
                )}
              </div>
              <div className="ticket-footer-right">
                <p><strong>NC Nº:</strong> {saleInfo.creditNoteData.numero}</p>
                {saleInfo.creditNoteData.comprobante_asociado?.numero && (
                  <p><strong>Factura asociada:</strong> {saleInfo.creditNoteData.comprobante_asociado.numero}</p>
                )}
                <p><strong>CAE NC:</strong> {saleInfo.creditNoteData.cae}</p>
                <p><strong>Vto. CAE:</strong> {formatVencimientoCAE(saleInfo.creditNoteData.vencimiento_cae)}</p>
              </div>
            </div>
          </>
        )}
        </div>
      </div>

      <div className="ticket-actions">
        <button className="print-btn" onClick={handlePrint}>
          🖨️ Imprimir Ticket
        </button>
        <button
          type="button"
          className="print-btn pdf-btn"
          onClick={handleDownloadPDF}
          disabled={pdfLoading}
        >
          {pdfLoading ? '⏳ Generando…' : '📄 Descargar PDF'}
        </button>
      </div>
    </div>
  );
};

export default ImprimirTicket;